<template>
  <div class="hot-topics-container" @wheel.prevent="handleContainerScroll">
    <!-- 加载状态改为绝对定位 -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner"></div>
    </div>

    <!-- 顶部导航栏 -->
    <nav class="top-nav">
      <div class="nav-left">
        <button class="action-button" @click="openSearch" title="搜索">
          <span class="action-icon">🔍</span>
          <span class="action-text">搜索</span>
        </button>
        <router-link to="/" class="logo"></router-link>
      </div>
      <div class="nav-right">
        <button class="nav-button" @click="showConfig = true">配置</button>
        <button class="nav-button" @click="goToHome">返回主页</button>
      </div>
    </nav>

    <!-- 配置面板 -->
    <div v-if="showConfig" class="config-overlay" @click.self="showConfig = false">
      <RefreshConfig @close="showConfig = false" />
    </div>

    <!-- 主要内容区域 -->
    <div class="content-grid">
      <!-- 左侧内容区 -->
      <div class="topics-section" ref="gridContainer">
        <div v-if="!hasNodes" class="no-data">
          暂无数据
        </div>
        <template v-else>
          <template v-for="node in visibleNodes" :key="node.id">
            <div class="topic-card"
                 :style="{
                   '--card-height': referenceCardHeight ? `${referenceCardHeight}px` : 'fit-content'
                 }"
                 draggable="true"
                 :data-node-id="node.id"
                 @dragstart="handleDragStart($event, node)"
                 @dragover.prevent="handleDragOver($event, node)"
                 @drop="handleDrop($event, node)">
              <div class="topic-header">
                <div class="source-info">
                  <img :src="'data:image/png;base64,' + node.siteIcon" class="source-icon" />
                  <h3>{{ node.siteName }} <span class="update-time">{{ formatTime(getLatestArticleTime(node.id)) }}</span></h3>
                </div>
                <div class="header-actions">
                  <button class="icon-btn" @click="toggleCardVisibility(node)" title="隐藏卡片">
                    <span class="icon">👁️</span>
                  </button>
                  <button class="icon-btn" @click="refreshNode(node)" title="刷新">
                    <span class="icon">🔄</span>
                  </button>
                </div>
              </div>
              
              <div class="topic-list" 
                   @wheel="handleCardWheel($event, node)"
                   @scroll="handleCardScroll($event, node)">
                <template v-if="node.isLoading">
                  <div class="loading-placeholder">
                    <div class="placeholder-item" v-for="n in 5" :key="n"></div>
                  </div>
                </template>
                <template v-else>
                  <div v-if="!getNodeArticles(node.id).length" class="no-articles">
                    暂无文章
                  </div>
                  <template v-else>
                    <div v-for="(article, idx) in getNodeArticles(node.id)" 
                         :key="`${node.id}-${article.idString}`" 
                         class="topic-item"
                         @click="openArticle(article, node.id)">
                      <span class="item-index" :class="{ 'top-three': idx < 3 }">{{ idx + 1 }}</span>
                      <span class="item-title">{{ article.title }}</span>
                      <span class="item-hot">{{ formatTime(article.pubDate) }}</span>
                      <span class="open-indicator">↗️</span>
                    </div>
                    <div v-if="loadingMore[node.id]" class="loading-more">
                      <div class="mini-spinner"></div>
                    </div>
                  </template>
                </template>
              </div>
            </div>
          </template>
          <!-- 显示隐藏的卡片 -->
          <div v-if="hasHiddenNodes" class="hidden-cards-section">
            <h3 class="hidden-cards-title">已隐藏的卡片</h3>
            <div class="hidden-cards-list">
              <div v-for="node in hiddenNodesList" 
                   :key="node.id" 
                   class="hidden-card-item"
                   @click="toggleCardVisibility(node)">
                <img :src="'data:image/png;base64,' + node.siteIcon" class="source-icon" />
                <span class="hidden-card-name">{{ node.siteName }}</span>
                <span class="show-card-btn">显示</span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- 右侧信息栏 -->
      <div class="info-section">
        <div class="user-info">
          <div class="avatar">👤</div>
          <div class="stats">
            <div>订阅源: {{ rssNodes.length }}</div>
            <div>更新时间: {{ lastUpdateTime }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/userStore';  
import { ref, onMounted, computed, watch, onUnmounted, provide, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import * as apiService from '@/services/apiService.js'
import { loadConfig, logger } from '@/config/refresh'
import RefreshConfig from './RefreshConfig.vue'

// 创建全局状态
const GLOBAL_REFRESH_STATE = Symbol('globalRefreshState')

export default {
  name: 'RssCardGrid',
  components: {
    RefreshConfig
  },
  
  setup() {
    const route = useRoute()
    const router = useRouter()
    const rssNodes = ref([])
    const nodeArticles = ref({})
    const lastUpdateTime = ref(new Date().toLocaleTimeString())
    const draggedNode = ref(null)
    const cardPositions = ref({})
    const loadingMore = ref({})
    const currentPage = ref({})
    const noMoreData = ref({})
    const isLoading = ref(false)
    const hiddenNodes = ref({})
    const showConfig = ref(false)
    const config = ref(loadConfig())
    const referenceCardHeight = ref(null)
    const hiddenNodesList = computed(() => {
      return rssNodes.value.filter(node => hiddenNodes.value[node.id])
    })
    const hasHiddenNodes = computed(() => {
      return Object.values(hiddenNodes.value).some(hidden => hidden)
    })

    // 获取全局状态
    const globalState = inject(GLOBAL_REFRESH_STATE, {
      refreshInterval: null,
      refreshQueue: ref([]),
      isRefreshing: ref(false),
      lastRefreshTime: ref(0)
    })

    // 提供全局状态给子组件
    provide(GLOBAL_REFRESH_STATE, globalState)

    // 计算属性
    const hasNodes = computed(() => {
      return Array.isArray(rssNodes.value) && rssNodes.value.length > 0
    })

    const visibleNodes = computed(() => {
      if (!hasNodes.value) return []
      return rssNodes.value
        .filter(node => node && node.id && !hiddenNodes.value[node.id])
        .sort((a, b) => {
          const posA = cardPositions.value[a.id] || { index: 0, grid: { col: 0, row: 0 } }
          const posB = cardPositions.value[b.id] || { index: 0, grid: { col: 0, row: 0 } }
          
          // 首先按行排序
          if (posA.grid.row !== posB.grid.row) {
            return posA.grid.row - posB.grid.row
          }
          // 如果行相同，则按列排序
          return posA.grid.col - posB.grid.col
        })
    })

    const getNodeArticles = (nodeId) => {
      if (!nodeId || !nodeArticles.value || !nodeArticles.value[nodeId]) {
        return [];
      }
      // 返回新的数组引用，确保 Vue 能检测到变化
      return [...(nodeArticles.value[nodeId] || [])]
        .sort((a, b) => {
          // 首先按发布时间排序
          const dateCompare = new Date(b.pubDate) - new Date(a.pubDate);
          if (dateCompare !== 0) return dateCompare;
          
          // 如果发布时间相同，则按 id 排序（使用自增的 id 字段）
          return b.id - a.id;
        });
    }

    const getLatestArticleTime = (nodeId) => {
      const articles = getNodeArticles(nodeId);
      if (!articles || articles.length === 0) return null;
      return articles[0].pubDate; // 因为已经按时间排序，第一个就是最新的
    }

    // 初始化所有状态
    const initializeStates = () => {
      nodeArticles.value = {}
      loadingMore.value = {}
      currentPage.value = {}
      noMoreData.value = {}
      hiddenNodes.value = {}
    }

    const setLoading = (loading) => {
      isLoading.value = loading
    }

    const fetchData = async () => {
      try {
        initializeStates()
        const authStore = useAuthStore();
        const isLoggedIn = authStore.isAuthenticated;
        const token = isLoggedIn ? authStore.token : undefined;
        // 第一步：加载节点基本信息
        const nodes = await apiService.fetchRssNodes(setLoading,token)
        if (!Array.isArray(nodes)) {
          console.error('节点数据格式错误:', nodes)
          return
        }
        
        rssNodes.value = nodes.filter(node => node && node.id)
        
        // 确保所有节点都有位置信息
        rssNodes.value.forEach(node => ensureCardPositions(node.id))
        
        loadCardPositions()
        loadHiddenNodes()
        
        // 第二步：为每个节点设置初始状态
        for (const node of rssNodes.value) {
          if (!node || !node.id) continue
          
          currentPage.value[node.id] = 1
          noMoreData.value[node.id] = false
          loadingMore.value[node.id] = false
          node.isExpanded = true
          node.isLoading = true
        }
        
        // 第三步：按顺序加载可见卡片的内容
        const visibleNodes = rssNodes.value.filter(node => !hiddenNodes.value[node.id])
        let isFirstCard = true
        
        for (const node of visibleNodes) {
          if (!node || !node.id) continue
          try {
            const response = await apiService.fetchFeedsBySite(
              node.id,
              currentPage.value[node.id],
              12,
              () => {}
            )
            
            if (response && Array.isArray(response.items)) {
              nodeArticles.value = {
                ...nodeArticles.value,
                [node.id]: response.items
              }
              
              // 只对第一个卡片进行高度计算
              if (isFirstCard) {
                isFirstCard = false
                await new Promise(resolve => setTimeout(resolve, 0))
                const cardElement = document.querySelector('.topic-card')
                if (cardElement) {
                  const topicList = cardElement.querySelector('.topic-list')
                  if (topicList) {
                    const headerHeight = 48
                    const cardHeight = 400
                    const contentHeight = cardHeight - headerHeight
                    topicList.style.height = `${contentHeight}px`
                  }
                }
              }
            } else {
              nodeArticles.value = {
                ...nodeArticles.value,
                [node.id]: []
              }
            }
          } catch (error) {
            console.error(`加载失败: ${node.siteName}`, error)
            nodeArticles.value = {
              ...nodeArticles.value,
              [node.id]: []
            }
          } finally {
            node.isLoading = false
            await new Promise(resolve => setTimeout(resolve, 300))
          }
        }
        
        lastUpdateTime.value = new Date().toLocaleTimeString()
        startAutoRefresh()

        // 添加窗口大小变化监听
        window.addEventListener('resize', handleResize)
      } catch (error) {
        console.error("Error fetching data:", error)
      }
    }

    const startAutoRefresh = () => {
      if (globalState.refreshInterval) {
        clearInterval(globalState.refreshInterval)
        globalState.refreshInterval = null
      }

      globalState.refreshQueue.value = [...new Set(visibleNodes.value)]
      globalState.isRefreshing.value = false

      const processRefreshQueue = async () => {
        const now = Date.now()
        if (now - globalState.lastRefreshTime.value < config.value.interval) {
          return
        }

        if (globalState.isRefreshing.value || globalState.refreshQueue.value.length === 0) {
          return
        }

        globalState.isRefreshing.value = true
        const node = globalState.refreshQueue.value.shift()

        try {
          await refreshNodeAuto(node)
        } catch (error) {
          console.error(`自动刷新节点 ${node.siteName} 失败:`, error)
        } finally {
          globalState.isRefreshing.value = false
          globalState.lastRefreshTime.value = Date.now()
          if (!globalState.refreshQueue.value.some(n => n.id === node.id)) {
            globalState.refreshQueue.value.push(node)
          }
        }
      }

      globalState.refreshInterval = setInterval(processRefreshQueue, config.value.interval)
      processRefreshQueue()
    }

    const refreshNodeAuto = async (node) => {
      if (!node || !node.id) return
      try {
        currentPage.value[node.id] = 1
        noMoreData.value[node.id] = false
        const response = await apiService.fetchFeedsBySite(
          node.id,
          currentPage.value[node.id],
          12,
          setLoading
        )
        
        nodeArticles.value = {
          ...nodeArticles.value,
          [node.id]: response.items || []
        }
        
        const latestTime = getLatestArticleTime(node.id);
        if (latestTime) {
          node.lastUpdate = latestTime;
        }
      } catch (error) {
        console.error(`自动刷新节点 ${node.siteName} 失败:`, error)
        throw error
      }
    }

    const refreshNode = async (node) => {
      if (!node || !node.id) return
      try {
        if (globalState.isRefreshing.value) {
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
        currentPage.value[node.id] = 1
        noMoreData.value[node.id] = false
        const response = await apiService.fetchFeedsBySite(
          node.id,
          currentPage.value[node.id],
          12,
          setLoading
        )
        
        nodeArticles.value = {
          ...nodeArticles.value,
          [node.id]: response.items || []
        }
        
        const latestTime = getLatestArticleTime(node.id);
        if (latestTime) {
          node.lastUpdate = latestTime;
        }
      } catch (error) {
        console.error(`手动刷新节点 ${node.siteName} 失败:`, error)
      }
    }

    const stopAutoRefresh = () => {
      if (globalState.refreshInterval) {
        clearInterval(globalState.refreshInterval)
        globalState.refreshInterval = null
      }
      globalState.refreshQueue.value = []
      globalState.isRefreshing.value = false
    }

    // 监听配置变化
    watch(config, (newConfig) => {
      logger.info('配置已更新，重新启动自动刷新')
      stopAutoRefresh()
      // 确保使用最新的配置
      globalState.config = { ...newConfig }
      startAutoRefresh()
    }, { deep: true })

    onMounted(() => {
      loadHiddenNodes()
      if (route.path !== '/card-view') {
        fetchData()
      }
      // 初始调用一次以设置正确的布局
      handleResize()
      // 添加窗口大小变化监听
      window.addEventListener('resize', handleResize)
    })

    onUnmounted(() => {
      stopAutoRefresh()
      window.removeEventListener('resize', handleResize)
    })

    // 监听路由变化
    watch(() => route.path, (newPath) => {
      if (newPath === '/card-view') {
        fetchData()
      } else {
        stopAutoRefresh()
      }
    }, { immediate: true })

    // 监听 visibleNodes 变化，确保队列同步
    watch(visibleNodes, (newNodes) => {
      if (globalState.refreshInterval) {
        globalState.refreshQueue.value = [...new Set(newNodes)]
      }
    })

    const formatTime = (timestamp) => {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      const now = new Date()
      const diffInSeconds = Math.floor((now - date) / 1000)
      
      if (diffInSeconds < 60) {
        return `${diffInSeconds}秒前`
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60)
        return `${minutes}分钟前`
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600)
        return `${hours}小时前`
      } else {
        const days = Math.floor(diffInSeconds / 86400)
        return `${days}天前`
      }
    }

    const loadCardPositions = () => {
      const savedPositions = localStorage.getItem('cardPositions')
      if (savedPositions) {
        cardPositions.value = JSON.parse(savedPositions)
      }
    }

    const saveCardPositions = () => {
      localStorage.setItem('cardPositions', JSON.stringify(cardPositions.value))
    }

    const handleDragStart = (event, node) => {
      draggedNode.value = node
      event.dataTransfer.effectAllowed = 'move'
    }

    // 添加向量计算相关的工具函数
    const vectorUtils = {
      // 计算两点之间的向量
      getVector(from, to) {
        return {
          x: to.x - from.x,
          y: to.y - from.y
        }
      },
      
      // 计算向量的长度
      getLength(vector) {
        return Math.sqrt(vector.x * vector.x + vector.y * vector.y)
      },
      
      // 计算向量的方向（角度）
      getAngle(vector) {
        return Math.atan2(vector.y, vector.x)
      },
      
      // 判断向量是否在指定象限
      isInQuadrant(vector, quadrant) {
        const angle = this.getAngle(vector)
        switch(quadrant) {
          case 1: return angle >= 0 && angle < Math.PI/2
          case 2: return angle >= Math.PI/2 && angle < Math.PI
          case 3: return angle >= -Math.PI && angle < -Math.PI/2
          case 4: return angle >= -Math.PI/2 && angle < 0
          default: return false
        }
      }
    }

    const handleDragOver = (event, node) => {
      if (draggedNode.value && draggedNode.value.id !== node.id) {
        event.dataTransfer.dropEffect = 'move'
        const rect = event.currentTarget.getBoundingClientRect()
        const center = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        }
        const mousePos = {
          x: event.clientX,
          y: event.clientY
        }
        
        // 计算鼠标位置相对于卡片中心的向量
        const vector = vectorUtils.getVector(center, mousePos)
        
        // 移除所有卡片的拖拽状态
        document.querySelectorAll('.topic-card').forEach(card => {
          card.classList.remove('drag-over-top', 'drag-over-bottom', 'drag-over-left', 'drag-over-right')
        })
        
        // 根据向量方向添加拖拽指示
        if (vectorUtils.isInQuadrant(vector, 1)) {
          event.currentTarget.classList.add('drag-over-right')
        } else if (vectorUtils.isInQuadrant(vector, 2)) {
          event.currentTarget.classList.add('drag-over-top')
        } else if (vectorUtils.isInQuadrant(vector, 3)) {
          event.currentTarget.classList.add('drag-over-left')
        } else if (vectorUtils.isInQuadrant(vector, 4)) {
          event.currentTarget.classList.add('drag-over-bottom')
        }
      }
    }

    const handleDrop = (event, targetNode) => {
      if (!draggedNode.value || draggedNode.value.id === targetNode.id) return;

      // 获取目标卡片的网格位置
      const targetRect = event.currentTarget.getBoundingClientRect();
      
      // 计算网格位置
      const gridCols = Math.floor(window.innerWidth / targetRect.width);
      const targetIndex = rssNodes.value.findIndex(n => n.id === targetNode.id);
      const draggedIndex = rssNodes.value.findIndex(n => n.id === draggedNode.value.id);
      
      // 根据边框指示的方向确定目标位置
      let targetPos = targetIndex;
      if (event.currentTarget.classList.contains('drag-over-right')) {
        targetPos = targetIndex + 1;
      } else if (event.currentTarget.classList.contains('drag-over-left')) {
        targetPos = targetIndex - 1;
      } else if (event.currentTarget.classList.contains('drag-over-top')) {
        targetPos = targetIndex - gridCols;
      } else if (event.currentTarget.classList.contains('drag-over-bottom')) {
        targetPos = targetIndex + gridCols;
      }
      
      // 确保目标位置在有效范围内
      if (targetPos >= 0 && targetPos < rssNodes.value.length) {
        // 创建新的数组，保持其他卡片位置不变
        const newNodes = [...rssNodes.value];
        
        // 移除被拖动的卡片
        const [draggedCard] = newNodes.splice(draggedIndex, 1);
        
        // 调整插入位置：如果目标位置在被拖动卡片之后，需要减1
        const adjustedTargetPos = targetPos > draggedIndex ? targetPos - 1 : targetPos;
        
        // 在目标位置插入被拖动的卡片
        newNodes.splice(adjustedTargetPos, 0, draggedCard);
        
        // 更新 rssNodes
        rssNodes.value = newNodes;
        
        // 更新所有卡片的位置信息
        rssNodes.value.forEach((node, index) => {
          const row = Math.floor(index / gridCols);
          const col = index % gridCols;
          
          cardPositions.value[node.id] = {
            index,
            grid: { col, row },
            vector: {
              x: col * targetRect.width,
              y: row * targetRect.height
            }
          };
        });
        
        // 保存位置信息
        saveCardPositions();
      }
      
      // 清理拖拽状态
      event.currentTarget.classList.remove('drag-over-top', 'drag-over-bottom', 'drag-over-left', 'drag-over-right');
      draggedNode.value = null;
    };

    const handleCardWheel = (event, node) => {
      if (!node || !node.id) return;
      
      const list = event.target;
      if (!list) return;
      
      // 检查是否在卡片列表内滚动
      const isInCardList = event.target.closest('.topic-list');
      if (!isInCardList) return;
      
      // 允许默认滚动行为
      event.stopPropagation();
    };

    const handleCardScroll = (event, node) => {
      if (!node || !node.id) return;
      
      const list = event.target;
      if (!list) return;
      
      // 检查是否在卡片列表内滚动
      const isInCardList = event.target.closest('.topic-list');
      if (!isInCardList) return;
      
      // 获取文章列表容器
      const articleList = list.querySelector('.topic-item');
      if (!articleList) return;
      
      // 计算内容高度和可视区域
      const contentHeight = list.scrollHeight;
      const viewportHeight = list.clientHeight;
      const scrollTop = list.scrollTop;
      
      // 计算是否接近底部（使用百分比而不是固定像素）
      const scrollPercentage = (scrollTop + viewportHeight) / contentHeight;
      const isNearBottom = scrollPercentage > 0.8; // 当滚动到 80% 时触发加载
      
      if (isNearBottom && !loadingMore.value[node.id] && !noMoreData.value[node.id]) {
        loadMoreArticles(node);
      }
    };

    const handleContainerScroll = (event) => {
      // 检查是否在卡片列表内滚动
      const isInCardList = event.target.closest('.topic-list');
      if (isInCardList) {
        // 在卡片内滚动时，阻止事件冒泡到容器
        event.stopPropagation();
        return;
      }
      
      // 只有在非卡片列表区域才允许页面滚动
      window.scrollBy(0, event.deltaY);
    };

    const openArticle = async (article, nodeId) => {
      try {
        await apiService.articleOpen(article.idString, nodeId, setLoading)
        const newWindow = window.open(article.link, '_blank')
        if (newWindow) {
          newWindow.focus()
        }
      } catch (error) {
        console.error('Error recording article open:', error)
      }
    }

    const loadHiddenNodes = () => {
      const savedHiddenNodes = localStorage.getItem('hiddenNodes')
      if (savedHiddenNodes) {
        hiddenNodes.value = JSON.parse(savedHiddenNodes)
      }
    }

    const saveHiddenNodes = () => {
      localStorage.setItem('hiddenNodes', JSON.stringify(hiddenNodes.value))
    }

    const toggleCardVisibility = async (node) => {
      hiddenNodes.value[node.id] = !hiddenNodes.value[node.id]
      saveHiddenNodes()
      
      // 如果显示卡片，重新加载数据
      if (!hiddenNodes.value[node.id]) {
        try {
          node.isLoading = true
          const response = await apiService.fetchFeedsBySite(
            node.id,
            1,
            12,
            () => {}
          )
          
          if (response && Array.isArray(response.items)) {
            nodeArticles.value = {
              ...nodeArticles.value,
              [node.id]: response.items
            }
          } else {
            nodeArticles.value = {
              ...nodeArticles.value,
              [node.id]: []
            }
          }
        } catch (error) {
          console.error(`加载失败: ${node.siteName}`, error)
          nodeArticles.value = {
            ...nodeArticles.value,
            [node.id]: []
          }
        } finally {
          node.isLoading = false
        }
      }
    }

    const goToHome = () => {
      router.push('/')
    }

    const openSearch = () => {
      router.push('/search')
    }

    // 确保节点有位置信息
    const ensureCardPositions = (nodeId) => {
      if (!cardPositions.value[nodeId]) {
        const nodeIndex = rssNodes.value.findIndex(n => n.id === nodeId);
        if (nodeIndex !== -1) {
          const cardWidth = 320; // 卡片宽度
          const cardHeight = 400; // 卡片高度
          const gridCols = Math.floor(window.innerWidth / cardWidth);
          
          cardPositions.value[nodeId] = {
            index: nodeIndex,
            grid: {
              col: nodeIndex % gridCols,
              row: Math.floor(nodeIndex / gridCols)
            },
            vector: {
              x: (nodeIndex % gridCols) * cardWidth,
              y: Math.floor(nodeIndex / gridCols) * cardHeight
            }
          };
        }
      }
    };

    const loadMoreArticles = async (node) => {
      if (!node || !node.id) return;
      if (loadingMore.value[node.id] || noMoreData.value[node.id]) return;

      try {
        loadingMore.value[node.id] = true;
        currentPage.value[node.id] = (currentPage.value[node.id] || 0) + 1;
        
        const response = await apiService.fetchFeedsBySite(
          node.id,
          currentPage.value[node.id],
          12,
          setLoading
        );
        
        if (response && response.items && response.items.length > 0) {
          if (!nodeArticles.value[node.id]) {
            nodeArticles.value[node.id] = [];
          }
          nodeArticles.value[node.id] = [
            ...nodeArticles.value[node.id],
            ...response.items
          ];
        } else {
          if (currentPage.value[node.id] > 1) {
            noMoreData.value[node.id] = true;
          }
        }
      } catch (error) {
        console.error(`加载更多文章失败: ${node.siteName}`, error);
        currentPage.value[node.id] = Math.max(1, (currentPage.value[node.id] || 1) - 1);
      } finally {
        loadingMore.value[node.id] = false;
      }
    };

    // 处理窗口大小变化
    const handleResize = () => {
      const container = document.querySelector('.topics-section')
      if (!container) return

      // 获取父容器的实际宽度
      const parentContainer = container.parentElement
      if (!parentContainer) return

      const containerWidth = parentContainer.getBoundingClientRect().width
      const cardWidth = 320 // 卡片宽度
      const gap = 24 // 卡片间距 (1.5rem = 24px)
      
      // 计算可以放置的列数，考虑最小间距
      const minGap = 16 // 最小间距
      const maxColumns = Math.max(1, Math.floor((containerWidth + minGap) / (cardWidth + minGap)))
      
      // 根据屏幕宽度动态调整列数
      let columns = maxColumns
      if (containerWidth >= 1536) { // 2K 及以上屏幕
        columns = Math.min(maxColumns, 6)
      } else if (containerWidth >= 1280) { // 1440p 屏幕
        columns = Math.min(maxColumns, 5)
      } else if (containerWidth >= 1024) { // 1080p 屏幕
        columns = Math.min(maxColumns, 4)
      } else if (containerWidth >= 768) { // 平板
        columns = Math.min(maxColumns, 3)
      } else { // 手机
        columns = Math.min(maxColumns, 2)
      }
      
      // 计算实际卡片宽度，使其填充可用空间
      const actualCardWidth = (containerWidth - (columns - 1) * gap) / columns
      
      // 更新网格布局
      container.style.gridTemplateColumns = `repeat(${columns}, 1fr)`
      
      // 更新所有卡片的宽度
      document.querySelectorAll('.topic-card').forEach(card => {
        card.style.maxWidth = `${actualCardWidth}px`
      })
      
      // 重新计算卡片位置
      rssNodes.value.forEach((node, index) => {
        if (!node || !node.id) return
        const row = Math.floor(index / columns)
        const col = index % columns
        
        cardPositions.value[node.id] = {
          index,
          grid: { col, row },
          vector: {
            x: col * (actualCardWidth + gap),
            y: row * (400 + gap) // 400px 是卡片高度
          }
        }
      })
      
      saveCardPositions()
    }

    // 修改 CSS 样式
    const style = document.createElement('style')
    style.textContent = `
      .hot-topics-container {
        min-height: 100vh;
        width: 100%;
        margin: 0;
        padding: 0;
        position: relative;
      }

      .content-grid {
        display: block;
        width: 100%;
        margin: 0;
        padding: 0;
      }

      .topics-section {
        display: grid;
        gap: 1.5rem;
        width: 100%;
        padding: 1.5rem;
        margin: 0;
        box-sizing: border-box;
        transition: all 0.3s ease;
      }
      
      .topic-list {
        overflow-y: auto !important;
        position: relative !important;
        flex: 1 !important;
        min-height: 0 !important;
        padding: 0.5rem 0 !important;
      }
      
      .topic-card {
        display: flex !important;
        flex-direction: column !important;
        height: 400px !important;
        width: 100% !important;
        margin: 0 !important;
        transition: all 0.3s ease !important;
      }

      .topic-header {
        flex-shrink: 0 !important;
        height: 3rem !important;
      }

      .topic-item {
        min-height: 2rem !important;
        padding: 0.5rem 1rem !important;
        border-bottom: 1px solid #4a4a4a !important;
        box-sizing: border-box !important;
      }

      .topic-item:last-child {
        border-bottom: none !important;
      }

      @media (min-width: 768px) {
        .topics-section {
          grid-template-columns: repeat(3, 1fr);
        }
      }

      @media (min-width: 1024px) {
        .topics-section {
          grid-template-columns: repeat(4, 1fr);
        }
      }

      @media (min-width: 1280px) {
        .topics-section {
          grid-template-columns: repeat(5, 1fr);
        }
      }

      @media (min-width: 1536px) {
        .topics-section {
          grid-template-columns: repeat(6, 1fr);
        }
      }
    `
    document.head.appendChild(style)

    return {
      rssNodes,
      nodeArticles,
      lastUpdateTime,
      isLoading,
      hasNodes,
      visibleNodes,
      getNodeArticles,
      loadingMore,
      noMoreData,
      currentPage,
      hiddenNodes,
      formatTime,
      refreshNode,
      openArticle,
      handleDragStart,
      handleDragOver,
      handleDrop,
      handleCardWheel,
      handleCardScroll,
      handleContainerScroll,
      toggleCardVisibility,
      hiddenNodesList,
      hasHiddenNodes,
      startAutoRefresh,
      stopAutoRefresh,
      showConfig,
      config,
      getLatestArticleTime,
      goToHome,
      openSearch,
      referenceCardHeight
    }
  }
}
</script>

<style scoped>
.hot-topics-container {
  min-height: 100vh;
  background-color: #1a1a1a;
  color: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}

.top-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background-color: #2d2d2d;
  border-bottom: 1px solid #3a3a3a;
  position: sticky;
  top: 0;
  z-index: 50;
  backdrop-filter: blur(8px);
}

.nav-left .logo {
  color: #fff;
  text-decoration: none;
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: -0.025em;
}

.nav-button {
  padding: 0.5rem 1rem;
  background-color: #4a4a4a;
  border: none;
  border-radius: 0.5rem;
  color: #fff;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.nav-button:hover {
  background-color: #5a5a5a;
}

.nav-button:active {
  transform: scale(0.98);
}

.content-grid {
  display: block;
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
}

.topics-section {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
  width: 100%;
  padding: 1.5rem;
}

.topic-card {
  background-color: #363636;
  border-radius: 1rem;
  overflow: hidden;
  height: 400px; /* 调整卡片总高度 */
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid #4a4a4a;
  position: relative;
  display: flex;
  flex-direction: column;
}

.topic-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.topic-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem; /* 调整头部内边距 */
  background-color: #424242;
  border-bottom: 1px solid #4a4a4a;
  flex-shrink: 0;
  height: 3rem; /* 固定头部高度 */
}

.source-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.source-icon {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  object-fit: cover;
}

.source-info h3 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #fff;
}

.update-time {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-left: 0.5rem;
}

.topic-list {
  padding: 0.5rem 0;
  height: calc(100% - 3rem); /* 减去头部高度 */
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #4a4a4a #363636;
  -webkit-overflow-scrolling: touch;
  position: relative;
  flex: 1;
  min-height: 0;
  background-color: #363636;
}

.topic-list::-webkit-scrollbar {
  width: 6px;
}

.topic-list::-webkit-scrollbar-track {
  background: #363636;
}

.topic-list::-webkit-scrollbar-thumb {
  background-color: #4a4a4a;
  border-radius: 3px;
}

.topic-list::-webkit-scrollbar-thumb:hover {
  background-color: #5a5a5a;
}

.topic-item {
  display: flex;
  align-items: flex-start;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  border-bottom: 1px solid #4a4a4a;
  position: relative;
  min-height: 2rem; /* 恢复之前的高度 */
  box-sizing: border-box;
  gap: 0.5rem;
}

.topic-item:last-child {
  border-bottom: none;
}

.topic-item:hover {
  background-color: #424242;
}

.topic-item:hover .open-indicator {
  opacity: 1;
}

.item-index {
  width: 1.25rem; /* 恢复之前的宽度 */
  text-align: center;
  color: #b0b0b0;
  font-size: 0.75rem; /* 恢复之前的字体大小 */
  font-weight: 500;
  flex-shrink: 0;
  padding-top: 0.125rem;
}

.item-index.top-three {
  color: #ff6b6b;
  font-weight: 600;
}

.item-title {
  flex: 1;
  font-size: 0.8125rem; /* 恢复之前的字体大小 */
  line-height: 1.4; /* 恢复之前的行高 */
  color: #ffffff;
  word-break: break-word;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-right: 0.5rem;
  font-weight: 500;
}

.item-hot {
  color: #b0b0b0;
  font-size: 0.75rem; /* 恢复之前的字体大小 */
  white-space: nowrap;
  flex-shrink: 0;
  padding-top: 0.125rem;
}

.open-indicator {
  opacity: 0;
  font-size: 0.75rem; /* 恢复之前的字体大小 */
  transition: opacity 0.2s ease;
  color: #b0b0b0;
  flex-shrink: 0;
  padding-top: 0.125rem;
}

.topic-item:hover .open-indicator {
  opacity: 1;
}

.refresh-btn {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.refresh-btn:hover {
  background-color: #4a4a4a;
  color: #fff;
}

.refresh-btn:active {
  transform: scale(0.95);
}

.toggle-container {
  display: flex;
  align-items: center;
  margin-right: 1rem;
  -webkit-tap-highlight-color: transparent;
}

.toggle-label {
  margin-right: 0.5rem;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 500;
}

.switch {
  position: relative;
  display: inline-block;
  width: 3rem;
  height: 1.5rem;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #4a4a4a;
  transition: .4s;
  border-radius: 1.5rem;
}

.slider:before {
  position: absolute;
  content: "";
  height: 1.25rem;
  width: 1.25rem;
  left: 0.125rem;
  bottom: 0.125rem;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #3b82f6;
}

input:checked + .slider:before {
  transform: translateX(1.5rem);
}

.loading-more {
  display: flex;
  justify-content: center;
  padding: 0.75rem;
  background-color: rgba(45, 45, 45, 0.5);
  position: sticky;
  bottom: 0;
  z-index: 1;
  backdrop-filter: blur(4px);
}

.mini-spinner {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.no-more-data {
  display: none;
}

.no-data {
  text-align: center;
  padding: 2rem;
  color: #9ca3af;
  font-size: 1rem;
  grid-column: 1 / -1;
}

.no-articles {
  text-align: center;
  padding: 1.5rem;
  color: #9ca3af;
  font-size: 0.875rem;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.spinner {
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid #fff;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .topics-section {
    grid-template-columns: 1fr;
  }

  .topic-card {
    margin: 0.5rem;
  }

  .nav-button {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
  }

  .toggle-container {
    margin-right: 0.5rem;
  }
}

/* 触摸设备优化 */
@media (hover: none) {
  .topic-card:hover {
    transform: none;
  }

  .topic-item:hover {
    background-color: transparent;
  }

  .topic-item:active {
    background-color: #3a3a3a;
  }

  .nav-button:hover {
    background-color: #4a4a4a;
  }

  .refresh-btn:hover {
    background-color: transparent;
  }

  .topic-item .open-indicator {
    opacity: 1;
  }
}

/* 拖拽相关样式 */
.topic-card.drag-over-top {
  border-top: 2px solid #3b82f6;
}

.topic-card.drag-over-bottom {
  border-bottom: 2px solid #3b82f6;
}

.topic-card.drag-over-left {
  border-left: 2px solid #3b82f6;
}

.topic-card.drag-over-right {
  border-right: 2px solid #3b82f6;
}

.topic-card.dragging {
  opacity: 0.5;
  transform: scale(0.95);
  box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
}

.topic-card[draggable="true"] {
  cursor: move;
  -webkit-user-drag: element;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.icon-btn {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-btn:hover {
  background-color: #4a4a4a;
  color: #fff;
}

.icon-btn:active {
  transform: scale(0.95);
}

.hidden-cards-section {
  margin-top: 2rem;
  padding: 1rem;
  background-color: #2d2d2d;
  border-radius: 1rem;
  border: 1px solid #3a3a3a;
}

.hidden-cards-title {
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 1rem;
}

.hidden-cards-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.hidden-card-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background-color: #3a3a3a;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.hidden-card-item:hover {
  background-color: #4a4a4a;
}

.hidden-card-name {
  color: #fff;
  font-size: 0.875rem;
}

.show-card-btn {
  color: #3b82f6;
  font-size: 0.875rem;
  font-weight: 500;
  margin-left: auto;
}

@media (max-width: 768px) {
  .hidden-cards-list {
    flex-direction: column;
  }
  
  .hidden-card-item {
    width: 100%;
  }
}

.config-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.loading-placeholder {
  padding: 0.5rem 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.placeholder-item {
  height: 2rem;
  margin: 0 1rem;
  background: linear-gradient(90deg, #424242 0%, #4a4a4a 50%, #424242 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.25rem;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* 响应式布局 */
@media (max-width: 768px) {
  .topics-section {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .topics-section {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1025px) {
  .topics-section {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style> 